import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { fetchOfferPopup } from "../services/api";
import { getProductImage } from "../data/images";

const DISMISS_PREFIX = "heshaura_offer_dismissed_";

// Admin-controlled festival / offer popup. Appears ~2.5s after load when the
// admin has enabled it and the current time is within its active window.
// Dismissal is remembered per offer "version" so it won't nag on every refresh.
export const OfferPopup = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let timer;
    fetchOfferPopup()
      .then((res) => {
        if (!res?.active) return;
        const key = DISMISS_PREFIX + (res.version || "v1");
        if (localStorage.getItem(key)) return;
        setData({ ...res.popup, _key: key });
        timer = setTimeout(() => setOpen(true), 2500);
      })
      .catch(() => {});
    return () => clearTimeout(timer);
  }, []);

  const close = () => {
    if (data?._key) localStorage.setItem(data._key, "1");
    setOpen(false);
  };

  if (!open || !data) return null;

  const banner = data.banner_image ? getProductImage(data.banner_image, data.title).src : null;
  const link = data.cta_link || "/shop";
  const external = /^https?:/i.test(link);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="offer-popup" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-brand-charcoal/60 backdrop-blur-sm" onClick={close} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-lg overflow-hidden border border-brand-gold/50 bg-brand-surface shadow-[0_40px_100px_-30px_rgba(28,25,23,0.6)]">
        <button
          onClick={close}
          data-testid="offer-popup-close"
          aria-label="Close offer"
          className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-brand-cream/90 text-brand-charcoal transition-colors duration-300 hover:bg-brand-gold hover:text-brand-cream"
        >
          <X className="h-4 w-4" />
        </button>
        {banner && (
          <div className="h-44 w-full overflow-hidden sm:h-52">
            <img src={banner} alt={data.title || "HESHAURA offer"} className="h-full w-full object-cover" data-testid="offer-popup-banner" />
          </div>
        )}
        <div className="px-8 py-9 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-gold">HESHAURA</p>
          {data.title && (
            <h3 className="mt-3 font-serif text-3xl font-medium leading-tight text-brand-charcoal" data-testid="offer-popup-title">
              {data.title}
            </h3>
          )}
          {data.subtitle && <p className="mt-3 text-sm leading-relaxed text-brand-stone">{data.subtitle}</p>}
          {data.discount_text && (
            <div
              className="mx-auto mt-5 inline-block border border-dashed border-brand-gold bg-brand-champagne px-6 py-2.5 font-serif text-xl tracking-[0.15em] text-brand-charcoal"
              data-testid="offer-popup-code"
            >
              {data.discount_text}
            </div>
          )}
          {data.cta_text && (
            <div className="mt-7">
              <a
                href={link}
                onClick={close}
                {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid="offer-popup-cta"
                className="btn-shimmer inline-flex items-center justify-center bg-brand-charcoal px-9 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-cream transition-colors duration-300 hover:bg-brand-gold"
              >
                {data.cta_text}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferPopup;
